import { Box, Typography, Autocomplete, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { DataGridPro, GridToolbarQuickFilter } from '@mui/x-data-grid-pro';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import ExcelUploadInput from '../../../../reusable-components/excel-input/ExcelUploadInput';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import { download } from '../../../../utils/download';
import ExcelDownloadTemplateButton from '../../../../reusable-components/excel-input/ExcelDownloadTemplate';

BulkUserUpload.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
}

export default function BulkUserUpload({ open, setOpen }) {
  const { enqueueSnackbar } = useSnackbar();
  const [sheets, setSheets] = useState(null);
  const [headerRow, setHeaderRow] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [excelUploadData, setExcelUploadData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setExcelData([]);
    setHeaderRow([]);
    setSheets(null);
  }, [open]);

  useEffect(() => {
    if (headerRow?.length) setExcelUploadData(data => ({
      ...data,
      emailCol: headerRow.find(cell => cell.name?.toLowerCase() === "email")?.value ?? data.emailCol,
      facilityCol: headerRow.find(cell => cell.name?.toLowerCase() === "facility")?.value ?? data.facilityCol,
      firstNameCol: headerRow.find(cell => cell.name?.toLowerCase() === "first name")?.value ?? data.firstNameCol,
      lastNameCol: headerRow.find(cell => cell.name?.toLowerCase() === "last name")?.value ?? data.lastNameCol,
      roleTemplateCol: headerRow.find(cell => cell.name?.toLowerCase() === "role template")?.value ?? data.roleTemplateCol,
      dontSendConfirmEmailCol: headerRow.find(cell => cell.name?.toLowerCase() === "dont send confirmation email")?.value ?? data.dontSendConfirmEmailCol,
      positionCol: headerRow.find(cell => cell.name?.toLowerCase() === "position")?.value ?? data.positionCol
    }))
  }, [headerRow]);

  const submit = () => {
    if (submitting) return;
    setSubmitting(true);
    axiosInstance.post("Admin/User/UploadUsersBulkSubmit", objectToFormData(excelUploadData))
      .then((res) => {
        enqueueSnackbar("Users upload started. You will receive an email with the results once done.", { variant: "success" });
        setOpen(false);
      })
      .catch(() => { enqueueSnackbar("Users upload failed", { variant: "error" }); })
      .finally(() => setSubmitting(false));
  }

  return <Dialog open={open} onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    setOpen(false);
  }} maxWidth="xl" fullWidth>
    <DialogTitle sx={{ textAlign: 'center' }}>
      Bulk User Upload/Edit
      <CloseIconButton onClick={() => setOpen(false)} />
    </DialogTitle>
    <DialogContent>
      <Grid sx={{ mt: 0 }} container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center">
            <ExcelUploadInput
              setSheets={setSheets}
              setFile={(file) => setExcelUploadData(data => ({ ...(data ?? {}), file }))}
              setSelectedSheet={(sheet) => setExcelUploadData(data => ({ ...(data ?? {}), sheet }))}
              setHeaderRow={setHeaderRow}
              setExcelData={setExcelData}
              selectedSheet={excelUploadData?.sheet}
              ignoreEmptyRows
            />
            <ExcelDownloadTemplateButton id={5} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography>Sheet</Typography>
          <Autocomplete
            fullWidth
            value={excelUploadData?.sheet ? { value: excelUploadData.sheet, label: excelUploadData.sheet } : null}
            onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), sheet: newValue?.value }))}
            options={sheets?.map(sheet => ({ value: sheet, label: sheet })) ?? []}
            {...SimpleAutocompleteProps}
          />
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={3}>
            <Typography>Email*</Typography>
            <Autocomplete
              value={excelUploadData?.emailCol ? headerRow.find(cell => cell.value === excelUploadData.emailCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), emailCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />             
          </Grid>
          <Grid item xs={3}>
            <Typography>Facility*</Typography>
            <Autocomplete
              value={excelUploadData?.facilityCol ? headerRow.find(cell => cell.value === excelUploadData.facilityCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), facilityCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>   
          <Grid item xs={3}>
            <Typography>First Name*</Typography>
            <Autocomplete
              value={excelUploadData?.firstNameCol ? headerRow.find(cell => cell.value === excelUploadData.firstNameCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), firstNameCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography>Last Name*</Typography>
            <Autocomplete
              value={excelUploadData?.lastNameCol ? headerRow.find(cell => cell.value === excelUploadData.lastNameCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), lastNameCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Position*</Typography>
            <Autocomplete
              value={excelUploadData?.positionCol ? headerRow.find(cell => cell.value === excelUploadData.positionCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), positionCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Don't Send Email</Typography>
            <Autocomplete
              value={excelUploadData?.dontSendConfirmEmailCol ? headerRow.find(cell => cell.value === excelUploadData.dontSendConfirmEmailCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), dontSendConfirmEmailCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Role Template</Typography>
            <Autocomplete
              value={excelUploadData?.roleTemplateCol ? headerRow.find(cell => cell.value === excelUploadData.roleTemplateCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), roleTemplateCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <DataGridPro
            gridId="admin-user-excel-upload"
            rows={excelData.length > 0 ? excelData.slice(1) : excelData}
            sx={{ height: 300, border: "none" }}
            columns={headerRow.length > 0 ? headerRow.map(data => ({ field: data.value, headerName: data.label, flex: 1 })) : []}
            getRowId={(row) => row.row}
            slots={{
              toolbar: () => <Box display="flex" justifyContent="right">
                <GridToolbarQuickFilter size="small" variant={"outlined"} inputProps={{ sx: { "padding": "3px" } }} />
              </Box>
            }}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={() => setOpen(false)}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!excelUploadData?.emailCol || !excelUploadData.facilityCol || !excelUploadData.firstNameCol ||
          !excelUploadData.lastNameCol || !excelUploadData.positionCol}
        sx={{ width: 150 }}
        onClick={submit}
        loading={submitting}
      >
        Save
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}
